import { HTMLAttributes, ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import styles from "./Modal.module.css";
import { cx } from "../utils";

export interface ModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    size?: "sm" | "md" | "lg" | "xl" | "fullscreen";
    className?: string;
    overlayClassName?: string;
    onCloseAutoFocus?: Dialog.DialogContentProps["onCloseAutoFocus"];
    children: ReactNode;
}

interface ModalHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
    title?: ReactNode;
    description?: ReactNode;
    onClose?: boolean | (() => void);
}

const sizeClasses = {
    sm: styles.sizeSm,
    md: styles.sizeMd,
    lg: styles.sizeLg,
    xl: styles.sizeXl,
    fullscreen: styles.sizeFullscreen
};

const ModalRoot = ({ open, onOpenChange, size = "md", className, overlayClassName, onCloseAutoFocus, children }: ModalProps) => (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
        <Dialog.Portal>
            <Dialog.Overlay className={cx(styles.overlay, overlayClassName)} />
            <Dialog.Content className={cx(styles.content, sizeClasses[size], className)} onCloseAutoFocus={onCloseAutoFocus}>
                {children}
            </Dialog.Content>
        </Dialog.Portal>
    </Dialog.Root>
);

const ModalHeader = ({ title, description, onClose = false, className, children, ...props }: ModalHeaderProps) => (
    <div className={cx(styles.header, className)} {...props}>
        <div>
            {title ? <Dialog.Title className={styles.title}>{title}</Dialog.Title> : null}
            {description ? <Dialog.Description className={styles.description}>{description}</Dialog.Description> : null}
            {children}
        </div>
        {onClose ? (
            <Dialog.Close asChild>
                <button type="button" className={styles.closeButton} onClick={typeof onClose === "function" ? onClose : undefined} aria-label="Close modal">
                    <X size={18} />
                </button>
            </Dialog.Close>
        ) : null}
    </div>
);

const ModalBody = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => <div className={cx(styles.body, className)} {...props} />;
const ModalFooter = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => <div className={cx(styles.footer, className)} {...props} />;

export const Modal = Object.assign(ModalRoot, {
    Header: ModalHeader,
    Body: ModalBody,
    Footer: ModalFooter
});
